import { useLocation } from 'react-router-dom';

interface DynamicPageTitle {
    title: string;
    description?: string;
}

export const usePageTitle = (basePage?: string): DynamicPageTitle => {
    const location = useLocation();
    const pathname = location.pathname;

    // Define route-specific titles
    const routeTitles: Record<string, DynamicPageTitle> = {
        // Auth pages
        '/login': { title: 'Connexion', description: 'Connectez-vous à votre espace Upercut CMS' },
        '/auth/login': { title: 'Connexion', description: 'Connectez-vous à votre espace Upercut CMS' },
        '/forgot-password': { title: 'Mot de passe oublié', description: 'Réinialisez votre mot de passe Upercut CMS' },
        '/auth/forgot-password': { title: 'Mot de passe oublié', description: 'Réinialisez votre mot de passe Upercut CMS' },
        '/reset-password': { title: 'Réinitialiser le mot de passe', description: 'Réinitialisez votre mot de passe avec le code de vérification' },
        '/auth/reset-password': { title: 'Réinitialiser le mot de passe', description: 'Réinitialisez votre mot de passe avec le code de vérification' },

        // Dashboard pages
        '/': { title: 'Tableau de bord', description: 'Vue d\'ensemble de votre espace Upercut CMS' },
        '/dashboard': { title: 'Tableau de bord', description: 'Vue d\'ensemble de votre espace Upercut CMS' },

        // Job Management
        '/job-offers': { title: 'Gestion des offres d\'emploi' },
        '/job-offers/create': { title: 'Créer une offre d\'emploi' },
        '/job-offers/templates': { title: 'Modèles d\'offres d\'emploi' },
        '/pro/dashboard': { title: 'Tableau de bord Pro', description: 'Vue d\'ensemble de votre espace Pro' },
        '/pro/applications': { title: 'Candidatures Pro' },
        '/pro/brands': { title: 'Marques Pro' },

        // Applications
        '/applications': { title: 'Candidatures' },

        // Talents
        '/talents': { title: 'Gestion des talents' },
        '/talents/create': { title: 'Ajouter un talent' },

        // Companies
        '/companies': { title: 'Gestion des entreprises' },
        '/companies/create': { title: 'Ajouter une entreprise' },

        // Brands
        '/brands': { title: 'Gestion des marques' },
        '/brands/create': { title: 'Créer une marque' },

        // Blog
        '/blog': { title: 'Articles de blog' },
        '/blog/create': { title: 'Créer un article' },

        // Pricing Tables
        '/pricing-tables': { title: 'Grilles tarifaires' },
        '/pricing-tables/create': { title: 'Créer une grille tarifaire' },

        // Upercut Management
        '/upercuts': { title: 'Gestion des utilisateurs Upercut' },
        '/upercuts/roles': { title: 'Gestion des rôles' },

        // Settings
        '/settings': { title: 'Paramètres du profil' },
        '/settings/profile': { title: 'Profil - Paramètres' },
        '/settings/photo': { title: 'Photo de profil - Paramètres' },
        '/settings/research': { title: 'Recherches - Paramètres' },
        '/settings/skill': { title: 'Compétences - Paramètres' },
        '/settings/formation': { title: 'Formation - Paramètres' },
        '/settings/experience': { title: 'Expérience - Paramètres' },
        '/settings/certification': { title: 'Certifications - Paramètres' },

        // Viewer
        '/viewer': { title: 'Contenu en lecture seule', description: 'Vue en lecture seule du contenu' },
    };

    // Check for exact match first
    if (routeTitles[pathname]) {
        return routeTitles[pathname];
    }

    // Check for dynamic routes (with parameters)
    for (const route in routeTitles) {
        // Convert route pattern to regex (e.g., '/job-offers/:slug' -> '/job-offers/[^/]+')
        const routeRegex = new RegExp('^' + route.replace(/:[\w]+/g, '[^/]+') + '$');
        if (routeRegex.test(pathname)) {
            const baseRoute = routeTitles[route];

            // Customize title based on specific patterns
            if (pathname.includes('/edit/')) {
                return { ...baseRoute, title: `Modifier ${baseRoute.title.toLowerCase()}` };
            }
            if (pathname.includes('/details/')) {
                return { ...baseRoute, title: `Détails ${baseRoute.title.toLowerCase()}` };
            }
            if (pathname.includes('/applications/')) {
                return { ...baseRoute, title: `Candidatures ${baseRoute.title.toLowerCase()}` };
            }

            return baseRoute;
        }
    }

    // Fallback to base page or default
    if (basePage) {
        return { title: basePage };
    }

    return { title: 'Upercut CMS' };
};

export default usePageTitle;
